import { Typography } from '@mui/material';
import { DocumentNode } from 'apollo-link';
import { pick } from 'ramda';
import React from 'react';
import { Query, QueryResult } from 'react-apollo';
import { isReactComponent } from '../..//crud-core/utils/TypescriptUtils';
import { TableEvents } from '../../crud-components/List/TableList';
import { offsetToPage, pageToOffset } from '../../crud-core/utils/Pagination';
import { OrderDirection } from '../../crud-core/utils/Sort';
import { Item, Resource } from '../Utils/genericTypes';

const defaultFirst = 20;
const defaultSkip = 0;

interface QueryVariables {
  first: number;
  skip?: number | null;
  orderBy?: string[];
  where?: {} | null;
}

interface ResourceListContainerProps extends TableEvents {
  messages?: {
    error?: JSX.Element;
    loading?: JSX.Element;
    nocontent?: JSX.Element;
  };
  query: DocumentNode;
  resourceConfig: Resource;
  variables: QueryVariables;
  params?: Record<string, any>;
  disableSort?: boolean;
}

type ResourceListContainerState = QueryVariables & {};

export const buildWhereFromParams = (params: Record<string, any> | undefined) => {
  let where: Item = {};
  if (params) {
    for (const filter in params) {
      if (params.hasOwnProperty(filter)) {
        if (filter.indexOf('-') !== -1) {
          const splittedFilters = filter.split('-');
          where = { [splittedFilters[splittedFilters.length - 1]]: params[filter] };
          splittedFilters.pop();
          // From CampaignList_newsletter-brandKey-test=CAP -> to { newsletter: { brandKey: { test: "CAP" } } }
          splittedFilters.reverse().forEach((splittedFilter) => {
            where = { [splittedFilter]: where };
          });
        } else {
          if (params[filter].length > 0) {
            where = Object.assign(
              {
                [filter]: params[filter],
              },
              where,
            );
          }
        }
      }
    }
  }

  return where;
};

class ResourceListContainer extends React.Component<
  ResourceListContainerProps,
  ResourceListContainerState
> {
  state: ResourceListContainerState = {
    first: defaultFirst,
    skip: 0,
    orderBy: this.props.variables.orderBy,
    where: this.props.variables.where,
  };

  static getDerivedStateFromProps(
    props: ResourceListContainerProps,
    state: ResourceListContainerState,
  ) {
    return {
      ...state,
      first: props.variables.first !== state.first ? state.first : props.variables.first,
      skip: props.variables.skip,
      orderBy: props.variables.orderBy !== state.orderBy ? state.orderBy : props.variables.orderBy,
      where: { ...props.variables.where, ...buildWhereFromParams(props.params) },
    };
  }

  componentDidUpdate(prevProps: ResourceListContainerProps) {
    if (
      (prevProps.params && this.props.params && prevProps.params !== this.props.params) ||
      (this.props.variables.where && prevProps.variables.where !== this.props.variables.where)
    ) {
      this.setState({
        where: {
          ...this.props.variables.where,
          ...buildWhereFromParams(this.props.params),
        },
      });
    }
  }

  render() {
    const { children, messages, query, resourceConfig, disableSort = false } = this.props;

    return (
      <Query query={query} variables={pick(['first', 'skip', 'orderBy', 'where'], this.state)}>
        {({ data, error, fetchMore, loading, variables: callbackVariables }: QueryResult) => {
          if (loading) {
            return (messages && messages.loading) || <Typography>Loading.</Typography>;
          }
          if (error) {
            return (messages && messages.error) || <Typography>Error.</Typography>;
          }
          if (!data) {
            return (messages && messages.nocontent) || <Typography>No content.</Typography>;
          }

          const {
            [resourceConfig.plural]: resourceItems,
            [`${resourceConfig.singular}Count`]: count,
          } = data;
          const [orderField, orderDirection] = (!disableSort &&
            callbackVariables.orderBy &&
            callbackVariables.orderBy.length > 0 &&
            callbackVariables.orderBy[0].split('_')) || ['', OrderDirection.ASC];

          const skip = callbackVariables.skip || defaultSkip;
          const first = callbackVariables.first || defaultFirst;

          return (
            <>
              {React.Children.map(children, (child) => {
                if (!child || !isReactComponent(child)) {
                  return;
                }
                return React.cloneElement(child, {
                  key: child.props.name,
                  items: resourceItems,
                  itemCount: count,
                  order: orderDirection.toLowerCase(),
                  orderBy: orderField,
                  page: offsetToPage(skip, first, 0),
                  resourceConfig,
                  rowsPerPage: first,
                  onChangePage: (page: number) => {
                    if (page * first < resourceItems.length) {
                      return;
                    }

                    if (child.props.onChangePage) {
                      child.props.onChangePage(page);
                    }

                    fetchMore({
                      query,
                      variables: {
                        ...callbackVariables,
                        skip: pageToOffset(page, first),
                      },
                      updateQuery: (prev, { fetchMoreResult }) => {
                        if (!fetchMoreResult) {
                          return prev;
                        }
                        return Object.assign({}, prev, {
                          [resourceConfig.plural]: [
                            ...prev[resourceConfig.plural],
                            ...fetchMoreResult[resourceConfig.plural],
                          ],
                        });
                      },
                    });
                  },
                  onChangeRowsPerPage: (rowsPerPage: number) => {
                    if (child.props.onChangeRowsPerPage) {
                      child.props.onChangeRowsPerPage(rowsPerPage);
                    }
                    this.setState({ first: rowsPerPage, skip: 0 });
                  },
                  onSort: (newOrderBy: string, newOrder: OrderDirection) => {
                    if (child.props.onSort) {
                      child.props.onSort(newOrderBy, newOrder);
                    }
                    this.setState({
                      orderBy: [`${newOrderBy}_${newOrder.toUpperCase()}`],
                    });
                  },
                  onSelectClick: (event: React.MouseEvent<HTMLTableRowElement>, id: string) => {
                    if (child.props.onSelectClick) {
                      child.props.onSelectClick(event, id);
                    }
                  },
                  onSelectAllClick: (
                    event: React.ChangeEvent<HTMLInputElement>,
                    checked: boolean,
                  ) => {
                    if (child.props.onSelectAllClick) {
                      child.props.onSelectAllClick(event, checked);
                    }
                  },
                });
              })}
            </>
          );
        }}
      </Query>
    );
  }
}

// eslint-disable-next-line import/no-default-export
export default ResourceListContainer;
